import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Badge, Button, InlineError, Input, Menu, Select, Skeleton } from '../../components/ui';
import { localIsoDate } from '../myTasks/types';
import {
  useCreateDealSection,
  useDealNotes,
  useDealPipeline,
  useDealSections,
  useDealStages,
  useDeals,
  useDeleteDealSection,
  useRenameDealSection,
  useReorderDealSections,
  useUpdateDeal,
} from './hooks';
import { useTapPeople } from '../projects/hooks';
import { useMoveDeal } from './dealMoves';
import { placeIntent, placeOf, placeOptions } from './kanbanColumns';
import { canEditModule, useMyPermissions } from '../auth/useMyPermissions';
import { AddDealRow } from './AddDealRow';
import { DealDrawer } from './DealDrawer';
import { AllDealsView } from './AllDealsView';
import { reorderSections, SECTION_DRAG_TYPE } from './sectionOrder';
import { starChanges } from './starMove';
import { DealKanban } from './DealKanban';
import { DealTasksView } from './DealTasksView';
import { dealErrorMessage, dealLoadMessage } from './queries';
import { PIPELINE_LABELS, dealsBySection, isOverdue, nextDealPosition } from './types';
import type { DropIntent } from './kanbanColumns';
import type { Deal, DealSection, Pipeline } from './types';

/**
 * Deals Monitoring — the List view.
 *
 * Sections are the organising structure and are ordinary rows: create, rename,
 * delete, and drag deals between them. That mirrors Project Sections because
 * the ask was to work the way Projects already do, rather than to invent a
 * second mental model for the same gesture.
 *
 * Five columns and no more. Every extra column here is one more thing to keep
 * current on a board meant for a daily glance; anything richer belongs in the
 * drawer. Section earned its place the hard way: moving a deal between
 * sections is the board's core gesture, and DRAG WAS THE ONLY WAY TO DO IT.
 * Kyu, using it: "it's very hard to drag one item to the next section...
 * it would be better if we can just have a one click from this view." So the
 * row says where the deal sits and the same control moves it — drag stays for
 * people who like it, demoted from the only way to the optional one.
 */

const PIPELINES: Pipeline[] = ['cg_deals', 'partnerships'];

/**
 * Kyu, seeing the Kanban ship beside the old tab row: "this is not a 'Board'
 * anymore, this is a 'List', the Kanban is the board."
 *
 * He is right, and the old name was only ever right because nothing else was
 * competing for it. Rows grouped under section headings are a list. Cards in
 * columns you drag between are a board.
 *
 * The internal keys stay 'list' | 'kanban' | 'tasks' and match the labels, so
 * nobody has to remember that the thing called list is keyed board. `dealKeys`
 * in hooks.ts keeps its own unrelated 'board' cache key — that names the data
 * behind every view, not one of them.
 */
const VIEW_LABELS = {
  list: 'List',
  kanban: 'Kanban',
  tasks: 'Tasks',
} as const;

export function DealsMonitoringView() {
  // ?pipeline= travels alongside ?deal= in every link a lead or company panel
  // makes (Drop 33). Without it a link to a partnerships deal would open the
  // CG board, find nothing, and silently show no drawer — a dead link dressed
  // as a working page. Read once, the same rule as ?deal below; anything that
  // is not exactly 'partnerships' falls back to the default board.
  const [pipelineParams] = useSearchParams();
  const [pipeline, setPipeline] = useState<Pipeline>(() =>
    pipelineParams.get('pipeline') === 'partnerships' ? 'partnerships' : 'cg_deals',
  );

  /* ── A deep link that names a deal but not a board ────────────────────────
   *
   * The comment above says anything that is not 'partnerships' falls back to
   * the default board. That is the right reading of the parameter and the
   * wrong outcome for a link that never had one: Sales Tasks knows a deal id
   * and nothing else, so every partnerships deal it linked to landed on the
   * CG board and opened nothing. Hanneza, 15 September: "it only lands on the
   * generic Deals tab list. The specific Deal Card modal does not open."
   *
   * So when a link arrives with ?deal= and no ?pipeline=, ask the database
   * which board that deal is on and switch to it. One row, once, and only on
   * that path. Fixing it here rather than in the links means the callers that
   * already forgot are fixed too, and the next one cannot make the mistake.
   *
   * `settled` latches: once the answer is in, or once somebody clicks a
   * pipeline tab themselves, the lookup never moves the board again.
   */
  const deepLinkDealId = pipelineParams.get('deal');
  const [settled, setSettled] = useState(
    () => deepLinkDealId === null || pipelineParams.get('pipeline') !== null,
  );
  const deepLink = useDealPipeline(settled ? null : deepLinkDealId);
  useEffect(() => {
    if (settled || deepLink.data === undefined) {
      return;
    }
    if (deepLink.data !== null) {
      setPipeline(deepLink.data);
    }
    setSettled(true);
  }, [settled, deepLink.data]);
  /**
   * All Deals sits in the PIPELINE row rather than the view row, because it is
   * a different set of deals rather than a different drawing of the same set.
   * It has no List/Kanban/Tasks of its own: sections belong to one pipeline
   * (deal_sections.pipeline) so nothing groups a mixed list, and a Kanban of
   * two stage vocabularies side by side would be two boards pretending to be
   * one. So the view row hides while it is open.
   */
  const [showingAll, setShowingAll] = useState(false);
  // List, Kanban, Tasks — three views of ONE set of deals. List groups by
  // section, which is how a day gets triaged; Kanban groups by stage, which is
  // where the pipeline is bunched. Kyu asked for both, for both pipelines:
  // "I need Kanban options here. for both CG and Partnerships."
  const [view, setView] = useState<'list' | 'kanban' | 'tasks'>('list');

  return (
    <>
      <div className="tracker-tab-nav" role="group" aria-label="Pipeline">
        {PIPELINES.map((key) => (
          <button
            key={key}
            type="button"
            className={`tracker-tab-button${!showingAll && pipeline === key ? ' tracker-tab-button--active' : ''}`}
            aria-pressed={!showingAll && pipeline === key}
            onClick={() => {
              setSettled(true);
              setShowingAll(false);
              setPipeline(key);
            }}
          >
            {PIPELINE_LABELS[key]}
          </button>
        ))}
        <button
          type="button"
          className={`tracker-tab-button${showingAll ? ' tracker-tab-button--active' : ''}`}
          aria-pressed={showingAll}
          onClick={() => setShowingAll(true)}
        >
          All Deals
        </button>
      </div>
      {showingAll ? null : (
        <div className="tracker-tab-nav" role="group" aria-label="View">
          {(['list', 'kanban', 'tasks'] as const).map((key) => (
            <button
              key={key}
              type="button"
              className={`tracker-tab-button${view === key ? ' tracker-tab-button--active' : ''}`}
              aria-pressed={view === key}
              onClick={() => setView(key)}
            >
              {VIEW_LABELS[key]}
            </button>
          ))}
        </div>
      )}
      {/* Keyed so switching pipeline starts clean rather than carrying the
          other pipeline's draft state across. */}
      {showingAll ? (
        <AllDealsView />
      ) : (
        <>
          {view === 'list' && <DealList key={pipeline} pipeline={pipeline} />}
          {view === 'kanban' && <DealKanban key={`${pipeline}-kanban`} pipeline={pipeline} />}
          {view === 'tasks' && <DealTasksPane key={`${pipeline}-tasks`} pipeline={pipeline} />}
        </>
      )}
    </>
  );
}

function DealTasksPane({ pipeline }: { pipeline: Pipeline }) {
  const tapPeople = useTapPeople(true);
  const people = (tapPeople.data ?? []).map((person) => ({
    id: `tap-person-${person.id}`,
    user_id: person.id,
    project_role: 'member' as const,
    display_name: person.display_name,
    email: person.email,
    avatar_url: person.avatar_url,
  }));
  return <DealTasksView pipeline={pipeline} people={people} />;
}

function DealList({ pipeline }: { pipeline: Pipeline }) {
  // The sales gate decides who reaches this view; the matrix decides whether
  // they may change anything once here. A read-only person keeps the board and
  // loses the drag handle, rather than dragging a card the database refuses.
  const permissions = useMyPermissions();
  const canEdit = canEditModule(permissions.data, 'deals');
  const deals = useDeals(pipeline);
  const sections = useDealSections(pipeline);
  const stages = useDealStages(pipeline);
  const dealIds = (deals.data ?? []).map((deal) => deal.id);
  const notes = useDealNotes(pipeline, dealIds);

  // Deal leads and @mentions draw from the whole TAP team; the sales gate
  // already decides who can reach this view at all.
  const tapPeople = useTapPeople(true);
  const people = (tapPeople.data ?? []).map((person) => ({
    id: `tap-person-${person.id}`,
    user_id: person.id,
    project_role: 'member' as const,
    display_name: person.display_name,
    email: person.email,
    avatar_url: person.avatar_url,
  }));

  const updateDeal = useUpdateDeal(pipeline);
  const move = useMoveDeal(pipeline);
  const createSection = useCreateDealSection(pipeline);
  const renameSection = useRenameDealSection(pipeline);
  const deleteSection = useDeleteDealSection(pipeline);
  const reorder = useReorderDealSections(pipeline);

  const [error, setError] = useState('');
  // Opened from a link, when there is one. The Inbox points a deal mention at
  // /deals?deal=<id>, the same shape a CG order notification already uses —
  // without it a notification lands somebody on a board and leaves them to
  // find the deal it was already about.
  //
  // Read once rather than kept in sync: closing the drawer should not have to
  // rewrite the URL, and re-reading would reopen it on the next render.
  const [dealParams] = useSearchParams();
  const [openDealId, setOpenDealId] = useState<string | null>(() => dealParams.get('deal'));
  // The comment a mention pointed at. Paired with the deal it arrived with, so
  // opening any OTHER deal afterwards does not try to highlight a comment that
  // is not in its thread. Read once, same as the deal above.
  const [deepLinkComment] = useState(() => {
    const deal = dealParams.get('deal');
    const comment = dealParams.get('comment');
    return deal !== null && comment !== null ? { deal, comment } : null;
  });
  const [newSection, setNewSection] = useState('');
  const [dragOver, setDragOver] = useState<string | null>(null);

  function report(cause: unknown) {
    setError(dealErrorMessage(cause instanceof Error ? cause.message : ''));
  }

  if (deals.isPending || sections.isPending) {
    return <Skeleton lines={5} label="Loading deals" />;
  }
  if (deals.isError || sections.isError) {
    // Same fix as the board: name the cause that was actually observed. See
    // dealLoadMessage in queries.ts for the incident behind this.
    const cause = deals.error ?? sections.error;
    return (
      <InlineError>{dealLoadMessage(cause instanceof Error ? cause.message : '')}</InlineError>
    );
  }

  const grouped = dealsBySection(deals.data);
  const openDeal = deals.data.find((deal) => deal.id === openDealId);
  const today = localIsoDate(new Date());
  // Deals whose section was deleted still need somewhere to be seen.
  const orphans = grouped.get('') ?? [];

  function dropSectionOn(draggedId: string, targetId: string) {
    setError('');
    const { changes } = reorderSections(sections.data ?? [], draggedId, targetId);
    if (changes.length === 0) return;
    reorder.mutate(changes, { onError: report });
  }

  function moveToSection(dealId: string, sectionId: string | null) {
    setError('');
    updateDeal.mutate({ dealId, changes: { section_id: sectionId } }, { onError: report });
  }

  function addSection() {
    const name = newSection.trim();
    if (name === '') {
      return;
    }
    setError('');
    createSection.mutate(
      { name, position: (sections.data?.at(-1)?.position ?? 0) + 10 },
      { onSuccess: () => setNewSection(''), onError: report },
    );
  }

  return (
    <>
      {error === '' ? null : <InlineError>{error}</InlineError>}

      {sections.data.map((section) => (
        <DealSectionGroup
          canEdit={canEdit}
          key={section.id}
          section={section}
          deals={grouped.get(section.id) ?? []}
          stages={stages.data ?? []}
          sections={sections.data}
          notes={notes.data ?? new Map()}
          today={today}
          pipeline={pipeline}
          dragOver={dragOver === section.id}
          onDragOver={() => setDragOver(section.id)}
          onDragLeave={() => setDragOver(null)}
          onDropDeal={(dealId) => {
            setDragOver(null);
            moveToSection(dealId, section.id);
          }}
          onDropSection={(draggedId) => {
            setDragOver(null);
            dropSectionOn(draggedId, section.id);
          }}
          onRename={(name) =>
            renameSection.mutate({ sectionId: section.id, name }, { onError: report })
          }
          onDelete={() => deleteSection.mutate(section.id, { onError: report })}
          onUpdateDeal={(dealId, changes) =>
            updateDeal.mutate({ dealId, changes }, { onError: report })
          }
          onToggleStar={(deal) =>
            updateDeal.mutate(
              { dealId: deal.id, changes: starChanges(deal, sections.data) },
              { onError: report },
            )
          }
          onMove={(deal, intent) => move(deal, intent, report)}
          onOpenDeal={setOpenDealId}
        />
      ))}

      {orphans.length === 0 ? null : (
        <section className="deal-section">
          <div className="deal-section-header">
            <h3 className="project-category-heading">No section ({orphans.length})</h3>
          </div>
          <p className="drawer-autosave-note">
            These deals lost their section when it was deleted. Pick a section on the row to move
            them.
          </p>
          <DealTable
            canEdit={canEdit}
            deals={orphans}
            stages={stages.data ?? []}
            sections={sections.data}
            notes={notes.data ?? new Map()}
            today={today}
            onUpdateDeal={(dealId, changes) =>
              updateDeal.mutate({ dealId, changes }, { onError: report })
            }
            onToggleStar={(deal) =>
              updateDeal.mutate(
                { dealId: deal.id, changes: starChanges(deal, sections.data) },
                { onError: report },
              )
            }
            onMove={(deal, intent) => move(deal, intent, report)}
            onOpenDeal={setOpenDealId}
          />
        </section>
      )}

      {openDeal === undefined ? null : (
        <DealDrawer
          key={openDeal.id}
          deal={openDeal}
          sections={sections.data}
          stages={stages.data ?? []}
          people={people}
          pipeline={pipeline}
          onClose={() => setOpenDealId(null)}
          highlightCommentId={
            deepLinkComment !== null && deepLinkComment.deal === openDeal.id
              ? deepLinkComment.comment
              : null
          }
        />
      )}

      <div className="deal-add-section">
        <Input
          label="New section"
          labelHidden
          placeholder="New section name"
          value={newSection}
          onChange={(event) => setNewSection(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              addSection();
            }
          }}
        />
        <Button variant="secondary" size="sm" onClick={addSection}>
          Add section
        </Button>
      </div>
    </>
  );
}

function DealSectionGroup({
  section,
  deals,
  stages,
  sections,
  notes,
  today,
  pipeline,
  canEdit,
  dragOver,
  onDragOver,
  onDragLeave,
  onDropDeal,
  onDropSection,
  onToggleStar,
  onRename,
  onDelete,
  onUpdateDeal,
  onMove,
  onOpenDeal,
}: {
  section: DealSection;
  deals: Deal[];
  stages: { id: string; name: string }[];
  /** Every section on the board, for the per-row move control. */
  sections: DealSection[];
  notes: Map<string, string>;
  today: string;
  pipeline: Pipeline;
  canEdit: boolean;
  dragOver: boolean;
  onDragOver: () => void;
  onDragLeave: () => void;
  onDropDeal: (dealId: string) => void;
  /** Another section was dropped on this one: put it here. */
  onDropSection: (draggedSectionId: string) => void;
  /** Star or unstar, which also moves the deal. See starMove.ts. */
  onToggleStar: (deal: Deal) => void;
  onRename: (name: string) => void;
  onDelete: () => void;
  onUpdateDeal: (
    dealId: string,
    changes: { stage_id?: string | null; section_id?: string | null; in_ceo_brief?: boolean },
  ) => void;
  onMove: (deal: Deal, intent: DropIntent) => void;
  onOpenDeal: (dealId: string) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState(section.name);
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <section
      className={`deal-section${dragOver ? ' deal-section--drag-over' : ''}`}
      onDragOver={(event) => {
        event.preventDefault();
        onDragOver();
      }}
      onDragLeave={onDragLeave}
      onDrop={(event) => {
        event.preventDefault();
        /* Two things can land here. A section carries its own type, checked
           first, so it is never read as a deal id off text/plain. */
        const sectionId = event.dataTransfer.getData(SECTION_DRAG_TYPE);
        if (sectionId !== '') {
          onDropSection(sectionId);
          return;
        }
        const dealId = event.dataTransfer.getData('text/plain');
        if (dealId !== '') {
          onDropDeal(dealId);
        }
      }}
    >
      <div className="deal-section-header">
        {/* Drag the section itself. Same explicit-and-guarded draggable as
            the deal grip, and the same reason: dragPermissions.test.ts. */}
        {canEdit ? (
          <span
            className="deal-section-grip"
            title={`Drag to move ${section.name}`}
            aria-hidden="true"
            draggable={canEdit}
            onDragStart={(event) => {
              event.dataTransfer.setData(SECTION_DRAG_TYPE, section.id);
              event.dataTransfer.effectAllowed = 'move';
            }}
          >
            ⋮⋮
          </span>
        ) : null}
        <button
          type="button"
          className="deal-section-toggle"
          aria-expanded={!collapsed}
          onClick={() => setCollapsed((value) => !value)}
        >
          {collapsed ? '▸' : '▾'}
        </button>
        {renaming ? (
          <Input
            label={`Rename ${section.name}`}
            labelHidden
            value={draftName}
            autoFocus
            onChange={(event) => setDraftName(event.target.value)}
            onBlur={() => {
              setRenaming(false);
              if (draftName.trim() !== '' && draftName !== section.name) {
                onRename(draftName.trim());
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.currentTarget.blur();
              }
            }}
          />
        ) : (
          <button
            type="button"
            className="deal-section-name"
            onClick={() => {
              setDraftName(section.name);
              setRenaming(true);
            }}
          >
            {section.name}
          </button>
        )}
        {/* Its own classes, not the Social Studio board's count and the app
            shell's topbar spacer. Borrowing them rendered correctly and tied
            this page to two modules it has nothing to do with — part of why
            it reads as a screen left over from somewhere else. */}
        <span className="deal-section-count">{deals.length}</span>
        <span className="deal-section-spacer" />
        {/* Was a ghost button in the brand accent, 28px, top-right of every
            section: a destructive action rendered in the colour reserved for
            primary actions, at primary prominence, on every section on the
            page. It goes in the overflow, in danger tone. */}
        {canEdit ? (
          <Menu
            triggerLabel={`Section actions for ${section.name}`}
            trigger={<span aria-hidden="true">•••</span>}
            items={[
              {
                label: 'Rename section',
                onSelect: () => {
                  setDraftName(section.name);
                  setRenaming(true);
                },
              },
              { label: 'Delete section…', danger: true, onSelect: () => setConfirmDelete(true) },
            ]}
          />
        ) : null}
      </div>

      {/* The confirmation is a visible panel, not a second click on a menu
          that has already closed. That trap cost a whole batch on the Social
          Studio delete and it is not being rebuilt here. */}
      {confirmDelete && (
        <div className="drawer-delete-confirm">
          <span>
            Delete <strong>{section.name}</strong>? Its deals lose their section but are kept.
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setConfirmDelete(false);
            }}
          >
            Keep it
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => {
              setConfirmDelete(false);
              onDelete();
            }}
          >
            Delete section
          </Button>
        </div>
      )}

      {collapsed ? null : (
        <>
          <DealTable
            canEdit={canEdit}
            deals={deals}
            stages={stages}
            sections={sections}
            notes={notes}
            today={today}
            onUpdateDeal={onUpdateDeal}
            onToggleStar={onToggleStar}
            onMove={onMove}
            onOpenDeal={onOpenDeal}
          />
          {/* The box asks who the deal is for, not what it is called. See
              addDeal.ts for why the name is derived rather than typed. */}
          <AddDealRow
            pipeline={pipeline}
            sectionId={section.id}
            position={nextDealPosition(deals)}
          />
        </>
      )}
    </section>
  );
}

/**
 * The grid every section shares.
 *
 * Four sections used to render four <table>s with table-layout: auto, so each
 * computed its widths from its own content: the Deal column measured 231, 182,
 * 248 and 287px across the four, and Notes swung between 79 and 380 depending
 * on whether that section happened to hold a long note. Nothing lined up with
 * anything.
 *
 * Declared once here and rendered into every table, with table-layout: fixed
 * in the stylesheet to make the browser honour it. Notes takes the flexible
 * track because it is the column that benefits from whatever is left over.
 */
function DealColumns() {
  return (
    <colgroup>
      <col style={{ width: '2.5rem' }} />
      <col style={{ width: '2.5rem' }} />
      <col style={{ width: '22%' }} />
      <col style={{ width: '11rem' }} />
      <col style={{ width: '11rem' }} />
      <col />
      <col style={{ width: '10rem' }} />
    </colgroup>
  );
}

function DealTable({
  deals,
  stages,
  sections,
  notes,
  today,
  canEdit,
  onUpdateDeal,
  onToggleStar,
  onMove,
  onOpenDeal,
}: {
  deals: Deal[];
  stages: { id: string; name: string }[];
  sections: DealSection[];
  notes: Map<string, string>;
  today: string;
  /** False when the matrix gives this person read-only access to Deals. */
  canEdit: boolean;
  onUpdateDeal: (
    dealId: string,
    changes: { stage_id?: string | null; section_id?: string | null; in_ceo_brief?: boolean },
  ) => void;
  onToggleStar: (deal: Deal) => void;
  /** A stage-or-outcome choice, applied the way a board drop is. */
  onMove: (deal: Deal, intent: DropIntent) => void;
  onOpenDeal: (dealId: string) => void;
}) {
  if (deals.length === 0) {
    return <p className="task-panel-text">No deals here yet.</p>;
  }
  return (
    <table className="ui-table deal-table">
      <DealColumns />
      <thead>
        <tr>
          <th>
            <span className="ui-visually-hidden">Reorder</span>
          </th>
          <th>
            <span className="ui-visually-hidden">In CEO brief</span>
          </th>
          <th>Deal</th>
          <th>Stage</th>
          <th>Section</th>
          <th>Notes</th>
          <th>Next action</th>
        </tr>
      </thead>
      <tbody>
        {deals.map((deal) => (
          <tr key={deal.id} className="deal-row">
            {/* The grip has its own column now. Dragging the name meant the
                thing you click to open a deal was also the thing that moved
                when you tried to select it. */}
            <td className="deal-cell--grip">
              {canEdit ? (
                <span
                  className="deal-grip"
                  aria-hidden="true"
                  /* Explicitly, not bare. A bare `draggable` is the tripwire
                     dragPermissions.test.ts exists to catch: it is correct
                     here only because of the guard around it, and that is
                     exactly what makes it easy to copy somewhere it is not. */
                  draggable={canEdit}
                  onDragStart={(event) => {
                    event.dataTransfer.setData('text/plain', deal.id);
                    event.dataTransfer.effectAllowed = 'move';
                  }}
                >
                  ⋮⋮
                </span>
              ) : null}
            </td>
            {/* Flagged for the CEO brief. Here rather than only in the
                drawer because this list IS the triage pass: deciding what
                Alex sees tomorrow should not cost opening every deal. */}
            <td className="deal-cell--brief">
              <button
                type="button"
                className={deal.in_ceo_brief ? 'deal-brief-star is-on' : 'deal-brief-star'}
                aria-pressed={deal.in_ceo_brief}
                disabled={!canEdit}
                title={
                  deal.in_ceo_brief
                    ? `Remove ${deal.name} from the CEO brief`
                    : `Show ${deal.name} in the CEO brief`
                }
                onClick={() => onToggleStar(deal)}
              >
                <span aria-hidden="true">{deal.in_ceo_brief ? '★' : '☆'}</span>
                <span className="ui-visually-hidden">
                  {deal.in_ceo_brief ? 'In the CEO brief' : 'Not in the CEO brief'}
                </span>
              </button>
            </td>
            <th scope="row">
              <button
                type="button"
                className="deal-name"
                title={deal.name}
                onClick={() => onOpenDeal(deal.id)}
              >
                {deal.name}
              </button>
            </th>
            <td>
              {/* Where the deal IS, the way the board says it: Won or Lost
                  when closed, the stage otherwise. Kyu, 8 September:
                  Hornetsecurity read Deck Sent here and sat in Won on the
                  board. Same options as the board's columns, same write as a
                  drop, through the same function. */}
              <Select
                label={`Stage for ${deal.name}`}
                labelHidden
                value={placeOf(deal)}
                options={placeOptions(stages)}
                onChange={(event) => onMove(deal, placeIntent(deal, event.target.value))}
              />
            </td>
            {/* The move Kyu asked for: one click from the row, same pattern
                as Stage beside it. The row leaves this table and appears
                under its new section on the refetch — the same thing a drop
                does, minus the drag. "No section" is a real choice, not an
                error state; the orphans block below the sections is where
                such deals stay visible. */}
            <td>
              <Select
                label={`Section for ${deal.name}`}
                labelHidden
                value={deal.section_id ?? ''}
                options={[
                  { value: '', label: 'No section' },
                  ...sections.map((row) => ({ value: row.id, label: row.name })),
                ]}
                onChange={(event) =>
                  onUpdateDeal(deal.id, {
                    section_id: event.target.value === '' ? null : event.target.value,
                  })
                }
              />
            </td>
            <td className="deal-note-cell">
              {/* A preview of the real thread, never an editable field: one
                  place for a note, so nothing can disagree with it.
                  The span is what carries the two-line clamp — a <td> cannot,
                  because -webkit-box on a table cell breaks the table. */}
              <span className="cell-clamp">
                {notes.get(deal.id) ?? <span className="deal-note-empty">No notes yet</span>}
              </span>
            </td>
            {/* A badge is for the exception. Every date used to wear one, so
                three ordinary dates and two overdue ones were the same shape
                and the danger tone had nothing to be louder than. On time is
                plain text now; overdue keeps the badge and is the only thing
                in the column wearing one. The date FORMAT is untouched —
                that is its own open question and not this drop's. */}
            <td>
              {deal.next_action_date === null ? (
                <span className="deal-note-empty">—</span>
              ) : isOverdue(deal.next_action_date, today) ? (
                <Badge tone="danger">{deal.next_action_date}</Badge>
              ) : (
                <span className="deal-next-plain">{deal.next_action_date}</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
